import { useEffect, useState } from "react"

import { request } from "shared/api/request"
import { Logger } from "shared/lib/Logger"
import { useEvent } from "shared/lib/useEvent"

import {
  FeedbackImageRules,
  FeedbackImageRulesSchema
} from "../types/FeedbackImageUpload"

const DEFAULT_RULES: FeedbackImageRules = {
  maxFileSizeMB: 10,
  maxFilesCount: 1,
  allowedExtensions: {
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"]
  }
}

export function useFeedbackImageRules() {
  const [rules, setRules] = useState<FeedbackImageRules>(DEFAULT_RULES)
  const [isLoading, setIsLoading] = useState(true)

  const fetchRules = useEvent(async () => {
    try {
      setIsLoading(true)
      const response = await request(
        `${import.meta.env.VITE_API_BASE}/feedback/rules`,
        { method: "GET" }
      )

      if (!response?.ok) {
        throw new Error("Failed to fetch feedback rules")
      }

      const json = await response.json()
      const parseResult = FeedbackImageRulesSchema.safeParse(json)

      if (!parseResult.success) {
        Logger.error(
          parseResult.error,
          "Failed to validate feedback rules response"
        )
        throw new Error("Invalid feedback rules format")
      }

      setRules(parseResult.data)
    } catch (error) {
      Logger.error(error, "Failed to fetch feedback rules")
    } finally {
      setIsLoading(false)
    }
  })

  useEffect(() => {
    fetchRules()
  }, [fetchRules])

  return {
    rules,
    isLoading
  }
}
